import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import CallIcon from '@mui/icons-material/Call'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined'
import Popover from '@mui/material/Popover'
import Autocomplete from 'react-google-autocomplete'
import { useShallow } from 'zustand/react/shallow'
import { buildLocationString, parseGoogleAddressData } from '../../../utils/utils'
import { useUserContext } from '../../../contexts/UserContext'
import { useModalsStore } from '../../../stores/modalsStore'
import { DealModal } from '../../PricingListPage/DealModal'
import { usePersonDetailStore } from '../stores/personDetailStore'
import { SOURCES, TIERS, salespersonOptions } from '../constants'
import { CrmTopbarPortal, useInCrmTopbar } from '../CrmLayout'
import { useIsMobile } from '../hooks/useIsMobile'
import { ProfitCount } from '../ReportsPage/ReportsPage'
import { StageTracker } from './StageTracker'
import { QuoteCard } from './QuoteCard'
import { MarkSoldModal, savedTradeIn } from './MarkSoldModal'
import { AddCarModal } from './AddCarModal'
import { AttachQuotesModal } from './AttachQuotesModal'
import { ConfirmDialog } from './ConfirmDialog'
import { AddToHouseholdModal } from '../HouseholdModal'
import { CurrentVehiclesSection } from './CurrentVehiclesSection'
import { ExtendLeaseModal } from './ExtendLeaseModal'
import { toISODate, toMDY } from './crmDates'
import { getDealType, getSoldVehicleLabel, tradeInToVehicle, wonOppToVehicle } from './wonOppToQuote'
import { EditableProfitPopover } from './EditableProfitPopover'
import './PersonDetailPage.scss'

const money = (v) => {
    const n = Number(v)
    if (!v || Number.isNaN(n)) return '$0'
    return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`
}
const monYr = (iso) => {
    if (!iso) return '—'
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

// MM/DD/YY for a history row. Two shapes arrive: `inception_date` is
// serialized MM/DD/YYYY, `created_at` is an ISO UTC timestamp. Both are read as
// calendar parts rather than through Date, so a UTC-midnight stamp can't render
// as the day before in a western timezone.
const shortMDY = (value) => {
    if (!value) return ''
    const mdy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(String(value))
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value))
    const [y, m, d] = mdy ? [mdy[3], mdy[1], mdy[2]] : iso ? [iso[1], iso[2], iso[3]] : []
    return y ? `${m.padStart(2, '0')}/${d.padStart(2, '0')}/${y.slice(2)}` : ''
}

const TYPE_LABEL = { LEASE: 'Lease', FINANCE: 'Finance', CASH: 'Cash' }
// QuoteCard's card-body themes, whose accents the Type text shares.
const TYPE_THEME = { LEASE: 'lease', FINANCE: 'purchase', CASH: 'cash' }

// One opportunity as its Lead History row: the date, the type, the car sold,
// the trade-in and the profit. A lone Purchase Vehicle sold nothing, so it is a
// "Trade In" with no new car, and the car it bought is its trade-in; a sale
// keeps its own type, as Current Vehicles reads it, trade-in or not. Blank
// cells render as "—".
const oppView = (o) => {
    if (o.status === 'WON') {
        const profit = money(o.total_profit)
        // A car bought from the customer, recorded as its own deal.
        const purchase = Boolean(o.is_vehicle_purchase)
        return {
            date: shortMDY(o.inception_date),
            type: purchase ? 'Trade In' : TYPE_LABEL[getDealType(o)] || '',
            newVehicle: purchase ? '' : getSoldVehicleLabel(o),
            tradeVehicle: (purchase ? o.sold_vehicle : o.trade_in_vehicle) || '',
            profit: profit === '$0' ? '' : profit,
        }
    }
    // A dead pursuit reads as "Lost"; the status stays LOST. It has no type,
    // car or profit, so a Lost pill takes the profit cell instead.
    if (o.status === 'LOST') {
        return { date: shortMDY(o.created_at), type: '', newVehicle: '', tradeVehicle: '', profit: '', lost: true }
    }
    return { date: shortMDY(o.created_at), type: o.stage_detail?.name || '', newVehicle: '', tradeVehicle: '', profit: '' }
}
// Open first (live work), then won, then lost.
const STATUS_RANK = { OPEN: 0, WON: 1, LOST: 2 }
const sortOpps = (opps) => [...opps].sort((a, b) =>
    (STATUS_RANK[a.status] ?? 3) - (STATUS_RANK[b.status] ?? 3) ||
    new Date(b.inception_date || b.created_at || 0) - new Date(a.inception_date || a.created_at || 0)
)

const DASH = '—'

// A car name that opens something: Edit Deal, or a trade-in's snapshot.
const OppLink = ({ label, onClick }) => (
    <button type="button" className="opp-link opp-name" title={label} aria-haspopup="dialog" onClick={onClick}>
        {label}
    </button>
)

// A row's cell contents, shared by the desktop table and the phone cards. A
// completed deal opens Edit Deal from its car's name: the new vehicle on a sale,
// the vehicle bought on a purchase, or — when the row has no name to click — a
// "⋯" in the New Vehicle cell. The amount opens the profit grid; the row itself
// stays inert.
const oppCells = (o, { onReopen, onDelete, onOpenProfit, onOpenTradeIn, onEditDeal }) => {
    const v = oppView(o)
    const isLost = o.status === 'LOST'
    const isWon = o.status === 'WON'
    const purchase = isWon && Boolean(o.is_vehicle_purchase)
    // The name Edit Deal hangs off; without one the "⋯" stands in.
    const editName = purchase ? v.tradeVehicle : v.newVehicle
    const needsFallback = isWon && !editName
    // Only a sale's Lease / Finance / Cash is coloured; Trade In, a stage
    // name and a lost row's "—" stay plain.
    const theme = isWon && !purchase ? TYPE_THEME[getDealType(o)] : null
    return {
        when: <span className="opp-date">{v.date || DASH}</span>,
        type: theme ? <span className={`opp-type-name ${theme}`}>{v.type}</span> : v.type || DASH,
        newVehicle: needsFallback ? (
            <button
                type="button"
                className="person-actions-btn opp-actions-btn"
                aria-label="Edit deal"
                aria-haspopup="dialog"
                onClick={() => onEditDeal(o)}
            >
                <MoreHorizIcon style={{ fontSize: 16 }} />
            </button>
        ) : isWon && !purchase ? <OppLink label={v.newVehicle} onClick={() => onEditDeal(o)} /> : DASH,
        // A purchase's car is its Edit Deal link; a sale's trade-in opens its details.
        tradeVehicle: v.tradeVehicle
            ? <OppLink label={v.tradeVehicle} onClick={() => (purchase ? onEditDeal(o) : onOpenTradeIn(o))} />
            : DASH,
        // Where the car was bought, as recorded at close. Never the lender.
        dealership: o.dealership ? <span className="opp-name" title={o.dealership}>{o.dealership}</span> : DASH,
        // The amount opens the deal's profit grid for editing.
        profit: v.lost ? <span className="opp-status opp-status-gray">Lost</span> : v.profit ? (
            // Reports' profit cell: the count badge, then the amount.
            <span className="profit-cell">
                <ProfitCount deal={o} />
                <button
                    type="button"
                    className="profit-link"
                    aria-haspopup="dialog"
                    onClick={(e) => onOpenProfit(e.currentTarget, o.id)}
                >
                    {v.profit}
                </button>
            </span>
        ) : <span className="profit-cell"><span>{DASH}</span></span>,
        // A lost row's own; a completed deal's live in Edit Deal.
        actions: (
            <>
                {isLost && onReopen && (
                    <button className="opp-reopen" onClick={() => onReopen(o.id)}>Reopen</button>
                )}
                {isLost && onDelete && (
                    <button className="opp-reopen opp-del" onClick={() => onDelete(o.id)}>Delete</button>
                )}
            </>
        ),
    }
}

// Narrowest Lead History the table fits: its fixed columns plus three ~116px
// name columns, which truncate (a lost row's Lost, Reopen and Delete need
// less). Below it, the cards. Kept under the 648px a 1225px window gives, so
// the switch doesn't flip back and forth around the 1224px sidebar swap.
const MIN_TABLE_WIDTH = 640

// The element's content width, tracked. Null until measured (and where
// ResizeObserver is missing), which keeps the table.
const useWidth = () => {
    const [el, setEl] = useState(null)
    const [width, setWidth] = useState(null)
    useEffect(() => {
        if (!el || typeof ResizeObserver === 'undefined') return undefined
        const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width))
        observer.observe(el)
        return () => observer.disconnect()
    }, [el])
    return [setEl, width]
}

// Lead History as a spreadsheet: Date | Type | New Vehicle | Trade in Vehicle |
// Dealership | Profit. A lost row has nothing from the trade-in on, so one cell
// spans those three: its "—", then Lost, Reopen and Delete as one group on the
// right edge. Phones, and any Lead History too narrow for that, get the same
// cells as stacked cards, as Reports does.
const LeadHistoryTable = (props) => {
    const [ref, width] = useWidth()
    const cards = props.isMobile || (width !== null && width < MIN_TABLE_WIDTH)
    return <div ref={ref}>{cards ? <LeadHistoryCards {...props} /> : <LeadHistoryRows {...props} />}</div>
}

const LeadHistoryCards = ({ opps, isMobile, ...handlers }) => {
    const rows = opps.map((o) => ({ o, c: oppCells(o, handlers) }))
    return (
        <div className="opp-cards">
            {rows.map(({ o, c }) => (
                <div className="opp-row opp-card" key={o.id}>
                    <div className="opp-card-head">
                        <div className="opp-when">{c.when}</div>
                        <div className="opp-actions">{c.actions}</div>
                    </div>
                    <dl className="opp-card-fields">
                        <div><dt>Type</dt><dd className="opp-type">{c.type}</dd></div>
                        <div><dt>New Vehicle</dt><dd className="opp-new">{c.newVehicle}</dd></div>
                        <div><dt>Trade in Vehicle</dt><dd className="opp-trade">{c.tradeVehicle}</dd></div>
                        <div><dt>Dealership</dt><dd className="opp-deal">{c.dealership}</dd></div>
                        <div><dt>Profit</dt><dd className="opp-profit">{c.profit}</dd></div>
                    </dl>
                </div>
            ))}
        </div>
    )
}

const LeadHistoryRows = ({ opps, isMobile, ...handlers }) => {
    const rows = opps.map((o) => ({ o, c: oppCells(o, handlers) }))
    return (
        <table className="opp-table">
            <thead>
                <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>New Vehicle</th>
                    <th>Trade in Vehicle</th>
                    <th>Dealership</th>
                    <th className="r">Profit</th>
                </tr>
            </thead>
            <tbody>
                {rows.map(({ o, c }) => (
                    <tr className="opp-row" key={o.id}>
                        <td className="opp-when">{c.when}</td>
                        <td className="opp-type">{c.type}</td>
                        <td className="opp-new">{c.newVehicle}</td>
                        {o.status === 'LOST' ? (
                            <td className="opp-trade" colSpan={3}>
                                <div className="opp-trade-lost">
                                    <span className="opp-trade-value">{c.tradeVehicle}</span>
                                    <span className="opp-lost-group">
                                        <span className="opp-profit">{c.profit}</span>
                                        <span className="opp-lost-actions">{c.actions}</span>
                                    </span>
                                </div>
                            </td>
                        ) : (
                            <>
                                <td className="opp-trade">{c.tradeVehicle}</td>
                                <td className="opp-deal">{c.dealership}</td>
                                <td className="opp-profit r">{c.profit}</td>
                            </>
                        )}
                    </tr>
                ))}
            </tbody>
        </table>
    )
}

// "⋯" menu on an open opportunity card: the three ways a pursuit ends. Every
// item belongs to that one card's opportunity, and closes the menu before it
// acts so the popover can't sit over the modal it opens. Only delete is
// destructive — Mark as Dead is the normal way to close a lost pursuit.
const OppMenu = ({ onMarkSold, onMarkDead, onDelete }) => {
    const [anchorEl, setAnchorEl] = useState(null)
    const pick = (action) => () => { setAnchorEl(null); action() }
    return (
        <>
            <MoreHorizIcon
                className="opp-menu-btn"
                style={{ fontSize: 18 }}
                onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget) }}
            />
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <div className="qcard-menu-item" onClick={pick(onMarkSold)}>Mark as Sold</div>
                <div className="qcard-menu-item" onClick={pick(onMarkDead)}>Mark as Dead</div>
                <div className="qcard-menu-item is-danger" onClick={pick(onDelete)}>Delete Opportunity</div>
            </Popover>
        </>
    )
}

// "⋯" menu for the person as a whole, beside Call / Email — as opposed to
// OppMenu above, which belongs to one opportunity card. "Add to household" shows
// only when the host passes it, i.e. when the person may join one.
const PersonActionsMenu = ({ onAddToHousehold, onDelete }) => {
    const [anchorEl, setAnchorEl] = useState(null)
    const pick = (action) => () => { setAnchorEl(null); action() }
    return (
        <>
            {/* A real button, unlike the card menus' bare icons: this one sits in
                the page header and has to be reachable from the keyboard. */}
            <button
                type="button"
                className="person-actions-btn"
                aria-label="More actions"
                aria-haspopup="menu"
                onClick={(e) => setAnchorEl(e.currentTarget)}
            >
                <MoreHorizIcon style={{ fontSize: 18 }} />
            </button>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {onAddToHousehold && (
                    <div className="qcard-menu-item" onClick={pick(onAddToHousehold)}>Add to household</div>
                )}
                <div className="qcard-menu-item is-danger" onClick={pick(onDelete)}>Delete person</div>
            </Popover>
        </>
    )
}

// Logged-activity kinds. The composer picks one; the feed colours + icons each.
const ACTIVITY_KINDS = [
    { value: 'NOTE', label: 'Note' },
    { value: 'CALL', label: 'Call' },
    { value: 'TEXT', label: 'Text' },
    { value: 'EMAIL', label: 'Email' },
]
const KIND_ICON = {
    CALL: <CallIcon style={{ fontSize: 15 }} />,
    TEXT: <ChatBubbleOutlineIcon style={{ fontSize: 15 }} />,
    EMAIL: <MailOutlineIcon style={{ fontSize: 15 }} />,
    NOTE: <StickyNote2OutlinedIcon style={{ fontSize: 15 }} />,
}
const ACTIVITY_FILTERS = [
    { label: 'All', value: 'all' },
    { label: 'Calls', value: 'CALL' },
    { label: 'Texts', value: 'TEXT' },
    { label: 'Emails', value: 'EMAIL' },
    { label: 'Notes', value: 'NOTE' },
]
const shortDate = (iso) => {
    if (!iso) return ''
    const d = new Date(iso)
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// The Person detail content, rendered by the full-page route (PersonDetailPage)
// on every viewport. Data comes from usePersonDetailStore, loaded by the host
// via fetchPerson. `onClose` navigates back out of the detail.
export const PersonDetailBody = ({ onClose }) => {
    const { person, opportunities, activities, vehicles, stages, loading, updateStage, saveNotes, savePerson, updateOpportunityField, markLost, createOpportunity, reopenOpportunity, deleteQuote, deleteOpportunity, deleteVehicle, extendVehicleLease, extendOpportunityLease, removeCurrentOpportunityVehicle, reverseSale, editSale, deleteSale, recordPurchase, deletePerson, logActivity, deleteActivity } =
        usePersonDetailStore(
            useShallow((state) => ({
                person: state.person,
                opportunities: state.opportunities,
                activities: state.activities,
                vehicles: state.vehicles,
                stages: state.stages,
                loading: state.loading,
                updateStage: state.updateStage,
                saveNotes: state.saveNotes,
                savePerson: state.savePerson,
                updateOpportunityField: state.updateOpportunityField,
                markLost: state.markLost,
                createOpportunity: state.createOpportunity,
                reopenOpportunity: state.reopenOpportunity,
                deleteQuote: state.deleteQuote,
                deleteOpportunity: state.deleteOpportunity,
                deleteVehicle: state.deleteVehicle,
                extendVehicleLease: state.extendVehicleLease,
                extendOpportunityLease: state.extendOpportunityLease,
                removeCurrentOpportunityVehicle: state.removeCurrentOpportunityVehicle,
                reverseSale: state.reverseSale,
                editSale: state.editSale,
                deleteSale: state.deleteSale,
                recordPurchase: state.recordPurchase,
                deletePerson: state.deletePerson,
                logActivity: state.logActivity,
                deleteActivity: state.deleteActivity,
            }))
        )

    const [notes, setNotes] = useState('')
    // The opportunity Mark as Sold is closing, held by id so the modal reads the
    // live one: a quote edited from inside it refreshes the person, and the rail
    // has to see those new quotes_detail rather than the object this was opened
    // with. Resolved below, not here.
    const [soldOppId, setSoldOppId] = useState(null)
    // The completed deal Edit Deal has open, held by id for the same reason.
    const [editOppId, setEditOppId] = useState(null)
    // The car modal while it is open: AddCarModal's props, where a null vehicle
    // means Add mode. A sold car adds the lock, its save and its deal link.
    const [carModal, setCarModal] = useState(null)
    // The lease being extended: { currentDate, onSave }, both already in the ISO
    // the date input speaks. Each source fills this in its own way, so the modal
    // never learns which kind of card opened it.
    const [leaseModal, setLeaseModal] = useState(null)
    // Pending destructive action: {title, message, onConfirm} for the in-app
    // confirm dialog (replaces window.confirm).
    const [confirmAction, setConfirmAction] = useState(null)
    const [attachOpp, setAttachOpp] = useState(null)
    const [householdOpen, setHouseholdOpen] = useState(false)
    // Which Lead History breakdown is open, and the amount it hangs off.
    const [profit, setProfit] = useState({ anchorEl: null, id: null })
    // Lead History is a table on desktop and stacked cards on a phone.
    const isMobile = useIsMobile()
    const [composerKind, setComposerKind] = useState('NOTE')
    const [composerBody, setComposerBody] = useState('')
    const [activityFilter, setActivityFilter] = useState('all')
    // The contact card's text fields while they are being edited. Held locally
    // so the field shows what the user typed, and `person` only ever holds a
    // value the server accepted.
    const [draft, setDraft] = useState({ name: '', phone_number: '', email: '', location: '' })
    // The salesperson chosen but not yet confirmed, so the select doesn't snap
    // back to the old rep for the length of the request. Cleared either way;
    // on failure that reveals the store's unchanged value again.
    const [pendingUser, setPendingUser] = useState(null)
    // Escape puts the server's value back and blurs. `.blur()` dispatches
    // synchronously while the reverting setDraft is still batched, so the commit
    // handler would otherwise read — and save — the abandoned draft.
    const revertingRef = useRef(false)

    // Seeded per person, so switching contacts can't carry a half-typed value
    // (or a pending salesperson) onto the next one.
    useEffect(() => {
        setNotes(person?.notes || '')
        setDraft({
            name: person?.name || '',
            phone_number: person?.phone_number || '',
            email: person?.email || '',
            location: buildLocationString(person),
        })
        setPendingUser(null)
        // A refetch blanks the person too, unmounting the breakdown's anchor.
        setProfit({ anchorEl: null, id: null })
    }, [person?.id])

    // The Salesperson dropdown's options. The backend scopes account_users to the
    // requester's account, but that is no longer narrow enough: reassigning a
    // pursuit to another rep is an admin act, and the serializer rejects a member
    // naming anyone but themselves with a 400 on `user`. Left unfiltered the
    // select would offer names it cannot save, and changeSalesperson would revert
    // with nothing on screen to explain it — so narrow it here too.
    const { profileState, isAccountAdmin } = useUserContext()
    const canReassign = isAccountAdmin()

    const submitActivity = () => {
        if (!composerBody.trim()) return
        logActivity({ kind: composerKind, body: composerBody })
        setComposerBody('')
    }

    // fetchPerson empties `opportunities` while it refetches, so the last resolved
    // one is held across that window — otherwise refreshing a quote from inside
    // Mark as Sold would unmount the modal under the rep mid-sale.
    const lastSoldOpp = useRef(null)
    const liveSoldOpp = soldOppId == null ? null : opportunities.find((o) => o.id === soldOppId)
    if (soldOppId == null) lastSoldOpp.current = null
    else if (liveSoldOpp) lastSoldOpp.current = liveSoldOpp
    const soldOpp = lastSoldOpp.current
    const lastEditOpp = useRef(null)
    const liveEditOpp = editOppId == null ? null : opportunities.find((o) => o.id === editOppId)
    if (editOppId == null) lastEditOpp.current = null
    else if (liveEditOpp) lastEditOpp.current = liveEditOpp
    const editOpp = lastEditOpp.current

    const wonOpps = opportunities.filter((o) => o.status === 'WON')
    // Current Vehicles alone. Every other reader below — Cars Sold, Lifetime
    // Profit, Client since, Lead History — keeps using `wonOpps`, because a car
    // the customer no longer has was still sold to them.
    const currentWonOpps = wonOpps.filter((o) => !o.vehicle_removed_at)
    const openOpps = opportunities.filter((o) => o.status === 'OPEN')
    const activeOpp = openOpps[0] || null
    const closedOpps = opportunities.filter((o) => o.status === 'WON' || o.status === 'LOST')
    const isClient = wonOpps.length > 0
    const isLead = openOpps.length > 0
    const renewalDue = openOpps.some((o) => o.is_renewal)
    const lifetimeProfit = wonOpps.reduce((s, o) => s + (Number(o.total_profit) || 0), 0)
    const visibleActivities = activityFilter === 'all'
        ? activities
        : activities.filter((a) => a.kind === activityFilter)

    const sinceLabel = isClient && wonOpps[0]?.inception_date
        ? `Client since ${monYr(wonOpps[0].inception_date)}`
        : `Added ${monYr(person?.created_at)}`

    // ── Editing the contact card ────────────────────────────────────────────
    //
    // Two destinations, deliberately. Name / Phone / Email are facts about the
    // contact and PATCH the Person. Salesperson / Source / Tier belong to the
    // pursuit and PATCH `activeOpp` — the newest open opportunity, the one this
    // card has always described. With nothing open there is no pursuit to edit,
    // so those three are disabled rather than falling back to closed history.

    // The value the store holds, normalised: `email` is nullable, and a null
    // would read as a change against the input's ''.
    const savedValue = (key) => person?.[key] || ''

    const editField = (key) => (e) => {
        const { value } = e.target
        setDraft((d) => ({ ...d, [key]: value }))
    }

    const commitField = (key) => async () => {
        if (revertingRef.current) {
            revertingRef.current = false
            return
        }
        const value = draft[key]
        if (value === savedValue(key)) return
        const saved = await savePerson({ [key]: value })
        // Rejected: show what the server still has rather than a value it
        // refused. savePerson leaves `person` untouched on failure.
        if (!saved) setDraft((d) => ({ ...d, [key]: savedValue(key) }))
    }

    // ZIP saves only from a picked suggestion, which carries the city, state and
    // county New Quote prices with. Typed text reverts; an emptied field clears.
    // Enter on text Google didn't match fires with no address: nothing to save.
    // Shown as the Calculator's ZIP input shows it.
    const savedLocation = buildLocationString(person)

    const selectZip = async (place) => {
        const parsed = parseGoogleAddressData(place.address_components)
        const { zipcode, city, state, county } = parsed
        setDraft((d) => ({ ...d, location: buildLocationString(parsed) }))
        const saved = Boolean(zipcode) && await savePerson({ zipcode, city, state, county })
        setDraft((d) => ({ ...d, location: saved ? buildLocationString(parsed) : savedLocation }))
    }

    const commitZip = async () => {
        if (draft.location === savedLocation) return
        const saved = draft.location === '' && await savePerson({ zipcode: '', city: '', state: '', county: '' })
        if (!saved) setDraft((d) => ({ ...d, location: savedLocation }))
    }

    const fieldKeyDown = (key) => (e) => {
        if (e.key === 'Enter') e.target.blur()
        if (e.key === 'Escape') {
            revertingRef.current = true
            setDraft((d) => ({ ...d, [key]: savedValue(key) }))
            e.target.blur()
        }
    }

    const canEditOpp = Boolean(activeOpp)
    // The pursuit's current rep, from the opportunity — never person.owner_detail,
    // which is the contact's creator and would snap this field back to the old
    // name after every save. With nothing open, the rep of the latest-closed sale
    // (Lead History's order), shown read-only: it is changed in its Edit Deal.
    const currentOwner = (activeOpp ?? sortOpps(wonOpps)[0])?.owner_detail || null
    // Matched on email, not id: the profile payload carries no user id (the app
    // identifies the current user by user_email throughout), and both sides of
    // this comparison are the same LowercaseEmailField, so it is exact rather
    // than a normalisation guess.
    const myEmail = profileState?.user_email
    const selectableUsers = canReassign
        ? profileState?.account_users
        : (profileState?.account_users ?? []).filter((u) => u?.email && u.email === myEmail)
    const accountOptions = salespersonOptions(selectableUsers)
    // The current rep is always listed, even if they have since dropped off the
    // account list, so a select can't render blank over a real value.
    const withOwner = (owner) => (owner && !accountOptions.some((u) => u.id === owner.id)
        ? [{ id: owner.id, label: owner.name || String(owner.id) }, ...accountOptions]
        : accountOptions)
    const salespeople = withOwner(currentOwner)
    const salespersonValue = pendingUser ?? currentOwner?.id ?? ''

    const changeSalesperson = async (e) => {
        const userId = Number(e.target.value)
        if (!activeOpp || !userId || userId === currentOwner?.id) return
        setPendingUser(userId)
        await updateOpportunityField(activeOpp.id, { user: userId })
        setPendingUser(null)
    }

    const changeOppField = (key) => (e) => {
        const { value } = e.target
        if (!activeOpp || value === activeOpp[key]) return
        updateOpportunityField(activeOpp.id, { [key]: value })
    }

    // Open the calculator in a new tab for a specific opportunity; when that tab
    // attaches its new quote it calls back here to refresh this person.
    const openNewQuoteTab = (opportunityId) => {
        const personId = person?.id
        window.onOpportunityQuoteCreated = () => {
            const store = usePersonDetailStore.getState()
            if (personId && store.person?.id === personId) store.fetchPerson(personId)
        }
        window.open(`/calculate?opportunity_id=${opportunityId}`, '_blank')
    }

    const openProfit = (anchorEl, id) => setProfit({ anchorEl, id })
    const closeProfit = () => setProfit({ anchorEl: null, id: null })

    // Delete is destructive and unlike Mark as Dead loses the record — confirm.
    const confirmDeleteOpportunity = (opportunityId) => setConfirmAction({
        title: 'Delete this opportunity?',
        message: 'This permanently removes it and its quotes. If you lost the '
            + 'deal, use "Mark as Dead" instead so it stays in your history and reports.',
        onConfirm: () => deleteOpportunity(opportunityId),
    })

    // Extending a lease. The two sources keep their dates in different fields and
    // different formats, so each converts to ISO here and the store converts back
    // on save. A hand-recorded car already stores ISO; an opportunity's
    // maturity_date arrives as MM/DD/YYYY.
    const openVehicleLease = (vehicle) => setLeaseModal({
        currentDate: toISODate(vehicle.expiration_date),
        onSave: (isoDate) => extendVehicleLease(vehicle.id, isoDate),
    })

    // A sold car as the car modal's vehicle, plus its read-only deal link.
    const soldCarModal = (opportunity) => {
        const { dealId, detailsLabel, ...vehicle } = wonOppToVehicle(opportunity)
        return {
            vehicle,
            ...(dealId != null && {
                detailsLabel,
                onViewDeal: () => {
                    setCarModal(null)
                    useModalsStore.getState().openModal({
                        element: DealModal,
                        id: 'DealModal',
                        props: {
                            id: dealId,
                            currentVehicle: {
                                inception_date: opportunity.inception_date ?? null,
                                maturity_date: opportunity.maturity_date ?? null,
                            },
                        },
                    })
                },
            }),
        }
    }

    // A sold car opens the car modal with the sale locked. Its own details save
    // via the opportunity (never /crm/vehicles: different ids); its deal opens read-only.
    const openOpportunityVehicle = (opportunity) => setCarModal({
        ...soldCarModal(opportunity),
        lockVehicle: true,
        onSave: async ({ vin, expirationDate, mileage, payoff, notes }) => {
            const saved = await updateOpportunityField(opportunity.id, {
                vin,
                maturity_date: expirationDate ? toMDY(expirationDate) : null,
                current_mileage: mileage,
                payoff,
                vehicle_notes: notes,
            })
            if (!saved) throw new Error('Could not save the car.')
        },
    })

    // A trade-in's snapshot, corrected in place through edit_sale. Never a
    // Current Vehicle, so it has no id of its own; replacing or removing the car
    // itself is Edit Deal's. From Lead History it links there; from inside Edit
    // Deal (a purchase's "Vehicle details") it opens over it instead.
    const openTradeIn = (opportunity, { fromEditDeal = false } = {}) => setCarModal({
        vehicle: tradeInToVehicle(opportunity),
        tradeIn: {
            onSave: (snapshot) => editSale(opportunity.id, { trade_in_vehicle_details: snapshot }),
            ...(!fromEditDeal && { onEditDeal: () => { setCarModal(null); setEditOppId(opportunity.id) } }),
        },
    })

    // Purchase Vehicle combines only into a completed sale, named as its Lead
    // History row names it. A separate purchase is not a sale to add to, and a
    // sale holds one trade-in at most.
    const purchaseTargets = sortOpps(wonOpps.filter((o) => !o.is_vehicle_purchase && !o.trade_in_vehicle))
        .map((o) => ({ id: o.id, label: `${shortMDY(o.inception_date) || '—'} — ${o.sold_vehicle || 'Vehicle'}` }))
    const purchase = (source) => ({
        targets: purchaseTargets,
        onRecord: (payload) => recordPurchase(source, payload),
    })
    const openVehiclePurchase = (vehicle) => setCarModal({
        vehicle, purchase: purchase({ vehicleId: vehicle.id }),
    })
    const openOpportunityPurchase = (opportunity) => setCarModal({
        ...soldCarModal(opportunity), purchase: purchase({ opportunityId: opportunity.id }),
    })

    const openOpportunityLease = (opportunity) => setLeaseModal({
        currentDate: toISODate(opportunity.maturity_date),
        onSave: (isoDate) => extendOpportunityLease(opportunity.id, isoDate),
    })

    // A hand-recorded car has no sale behind it, so removing one deletes the
    // record outright rather than just detaching a worksheet — hence no promise
    // here about history that does not exist.
    const confirmDeleteVehicle = (vehicleId) => setConfirmAction({
        title: 'Remove vehicle?',
        message: 'This will remove this vehicle from Current Vehicles.',
        confirmLabel: 'Remove Vehicle',
        onConfirm: () => deleteVehicle(vehicleId),
    })

    // A sold car: the sale stays, only the "they still drive it" claim goes.
    const confirmRemoveOpportunityVehicle = (opportunity) => setConfirmAction({
        title: 'Remove vehicle?',
        message: 'This removes the vehicle from Current Vehicles only. The sale stays in '
            + 'Lead History, and its profit still counts in Cars Sold, Lifetime Profit '
            + 'and Reports.',
        confirmLabel: 'Remove Vehicle',
        onConfirm: () => removeCurrentOpportunityVehicle(opportunity.id),
    })

    // The sale itself goes (the opportunity reopens as a lead). The inverse of
    // the message above. Shared by the Current Vehicle card and the WON Lead
    // History row.
    const confirmReverseSale = (opportunity) => setConfirmAction({
        title: 'Delete vehicle and sale history?',
        message: 'This removes the vehicle from Current Vehicles and the sale from Lead '
            + 'History. Its profit no longer counts in Cars Sold, Lifetime Profit or Reports.',
        confirmLabel: 'Delete Vehicle and history',
        onConfirm: () => reverseSale(opportunity.id),
    })

    // Delete Deal: the sale goes for good. A trade-in whose car can go back
    // makes the rep choose; one that can't says so rather than offering it.
    const confirmDeleteDeal = (opportunity) => {
        const trade = savedTradeIn(opportunity)
        const gone = 'This permanently deletes the sale. Its profit no longer counts in '
            + 'Cars Sold, Lifetime Profit or Reports.'
        const remove = async (restoreTradeIn) => {
            try {
                await deleteSale(opportunity.id, restoreTradeIn)
                setEditOppId(null)
            } catch (e) {
                /* the interceptor toasts it; Edit Deal stays open */
            }
        }
        if (trade && opportunity.trade_in_restorable) {
            setConfirmAction({
                title: 'Delete this deal?',
                message: `This permanently deletes the sale. Should ${trade.label} go back to Current Vehicles?`,
                confirmLabel: 'Delete deal',
                choices: [
                    { value: true, label: `Delete and return ${trade.label} to Current Vehicles` },
                    { value: false, label: 'Delete without returning it' },
                ],
                onConfirm: (restore) => remove(restore),
            })
        } else if (trade) {
            setConfirmAction({
                title: 'Delete this deal?',
                message: `This permanently deletes the sale. Its trade-in, ${trade.label}, `
                    + "can't be returned to Current Vehicles automatically.",
                confirmLabel: 'Delete deal',
                onConfirm: () => remove(false),
            })
        } else {
            setConfirmAction({
                title: 'Delete this deal?',
                message: gone,
                confirmLabel: 'Delete deal',
                onConfirm: () => remove(null),
            })
        }
    }

    // Deleting the contact takes their whole record with it, closed deals
    // included — which is what moves the reports. Product's call: duplicates
    // arrive with imports and the rep needs a way to clear them, so unlike an
    // opportunity there is no status that blocks this. `onClose` is the page's
    // own back navigation, run only once the server has confirmed.
    const confirmDeletePerson = () => setConfirmAction({
        title: 'Delete this person?',
        message: `This permanently deletes ${person?.name || 'this person'}, including all `
            + 'associated opportunities, deal history, activity, and vehicles. '
            + 'Historical reports may change. This cannot be undone.',
        confirmLabel: 'Delete person',
        onConfirm: async () => { if (await deletePerson()) onClose() },
    })

    // The serializer's flags, not isClient / isLead: those count only the
    // opportunities this viewer sees, while the server checks them all.
    const canJoinHousehold = Boolean(person && !person.household && (person.is_client || person.is_lead))

    // With the CRM tab bar, Back sits beside the contact card, so there is no
    // subheader row; phones keep the subheader. Call / Email / ⋯ live in the
    // contact card now, each beside the field it acts on, so neither the tab bar
    // nor the subheader carries person actions.
    const inBar = useInCrmTopbar()
    const backButton = (
        <button className="cdv-back" onClick={onClose} aria-label="Back">
            <ArrowBackIcon style={{ fontSize: 18 }} />
        </button>
    )

    return (
        <div className="cdv cdv-full">
            <CrmTopbarPortal
                fallback={(
                    <div className="subheader">
                        {!loading && backButton}
                        <span className="bc-current">{person?.name || (loading ? 'Loading…' : 'Contact')}</span>
                        {isClient && <span className="id-badge id-badge-green">Active Client</span>}
                        {isLead && <span className="id-badge id-badge-blue">Active Lead</span>}
                        {renewalDue && <span className="id-badge" style={{ background: '#FFF4E8', color: '#d07020' }}>Renewal due</span>}
                    </div>
                )}
            >
                {null}
            </CrmTopbarPortal>

            {!person ? (
                // While loading, only the text; Back returns with "Not found."
                <div className="cd-loading">{inBar && !loading && backButton}{loading ? 'Loading…' : 'Not found.'}</div>
            ) : (
                <div className={`cpage${inBar ? ' with-back' : ''}`}>
                    {inBar && backButton}
                    <div className="left-col">
                        <div className="panel">
                            <div className="panel-body">
                                {/* The ⋯ acts on the person, so it sits with their
                                    name rather than with any one field. */}
                                <div className="contact-head">
                                    <div className="contact-head-main">
                                        <input
                                            className="contact-name-input"
                                            value={draft.name}
                                            onChange={editField('name')}
                                            onBlur={commitField('name')}
                                            onKeyDown={fieldKeyDown('name')}
                                            aria-label="Name"
                                        />
                                        <div className="contact-stage-row">
                                            {isClient && <span className="stage-pill stage-pill-green">Client</span>}
                                            {isLead && <span className="stage-pill">Active Lead</span>}
                                            {renewalDue && <span className="stage-pill stage-pill-orange">Renewal due</span>}
                                            {/* Only sent for a household the viewer may open. */}
                                            {person.household_detail && (
                                                <Link
                                                    className="stage-pill stage-pill-household"
                                                    to={`/crm/people/households/${person.household_detail.id}`}
                                                    title={`Household: ${person.household_detail.name}`}
                                                >
                                                    <HomeOutlinedIcon />
                                                    <span>{person.household_detail.name}</span>
                                                </Link>
                                            )}
                                            <span className="contact-added">{sinceLabel}</span>
                                        </div>
                                    </div>
                                    <PersonActionsMenu
                                        onAddToHousehold={canJoinHousehold ? () => setHouseholdOpen(true) : undefined}
                                        onDelete={confirmDeletePerson}
                                    />
                                </div>
                                <div className="contact-fields">
                                    <div className="contact-field">
                                        <div className="field-label">Salesperson</div>
                                        <select
                                            className="contact-field-input contact-field-select"
                                            value={salespersonValue}
                                            onChange={changeSalesperson}
                                            disabled={!canEditOpp || !canReassign}
                                            aria-label="Salesperson"
                                        >
                                            {salespersonValue === '' && <option value="">—</option>}
                                            {salespeople.map((u) => (
                                                <option key={u.id} value={u.id}>{u.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="contact-divider" />
                                    {/* Call / Email sit beside the value they dial or
                                        write to, and follow the saved person rather
                                        than the draft: a half-typed number is not
                                        something to hand the dialler. */}
                                    <div className="contact-field">
                                        <div className="field-label">Phone</div>
                                        <div className="field-value-row">
                                            <input
                                                className="contact-field-input"
                                                value={draft.phone_number}
                                                onChange={editField('phone_number')}
                                                onBlur={commitField('phone_number')}
                                                onKeyDown={fieldKeyDown('phone_number')}
                                                placeholder="—"
                                                aria-label="Phone"
                                            />
                                            {person.phone_number && <a className="cbtn-outline" href={`tel:${person.phone_number}`}>Call</a>}
                                        </div>
                                    </div>
                                    <div className="contact-field">
                                        <div className="field-label">Email</div>
                                        <div className="field-value-row">
                                            <input
                                                className="contact-field-input"
                                                value={draft.email}
                                                onChange={editField('email')}
                                                onBlur={commitField('email')}
                                                onKeyDown={fieldKeyDown('email')}
                                                placeholder="—"
                                                aria-label="Email"
                                            />
                                            {person.email && <a className="cbtn-outline" href={`mailto:${person.email}`}>Email</a>}
                                        </div>
                                    </div>
                                    <div className="contact-field">
                                        <div className="field-label">ZIP Code</div>
                                        <Autocomplete
                                            className="contact-field-input"
                                            apiKey={process.env.REACT_APP_GOOGLE_API_KEY}
                                            value={draft.location}
                                            onChange={editField('location')}
                                            onBlur={commitZip}
                                            onPlaceSelected={selectZip}
                                            options={{
                                                types: ['postal_code'],
                                                componentRestrictions: { country: 'us' },
                                                fields: ['address_components', 'name'],
                                            }}
                                            placeholder="Enter 5-digit zipcode"
                                            aria-label="ZIP Code"
                                        />
                                    </div>
                                    <div className="contact-divider" />
                                    <div className="contact-field-row">
                                        <div className="contact-field">
                                            <div className="field-label">Source</div>
                                            <select
                                                className="contact-field-input contact-field-select"
                                                value={activeOpp?.source || ''}
                                                onChange={changeOppField('source')}
                                                disabled={!canEditOpp}
                                                aria-label="Source"
                                            >
                                                {!canEditOpp && <option value="">—</option>}
                                                {SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                            </select>
                                        </div>
                                        <div className="contact-field">
                                            <div className="field-label">Tier</div>
                                            <select
                                                className="contact-field-input contact-field-select"
                                                value={activeOpp?.tier || ''}
                                                onChange={changeOppField('tier')}
                                                disabled={!canEditOpp}
                                                aria-label="Tier"
                                            >
                                                {!canEditOpp && <option value="">—</option>}
                                                {TIERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="contact-divider" />
                                    <div className="contact-field-row">
                                        {/* Both derived from the WON opportunities above and deliberately
                                            read-only: a stored copy would immediately disagree with
                                            Reports, which sums the same rows. */}
                                        <div className="contact-field"><div className="field-label">Lifetime Profit</div><input className="contact-field-input" value={money(lifetimeProfit)} aria-label="Lifetime Profit" readOnly /></div>
                                        <div className="contact-field"><div className="field-label">Cars Sold</div><input className="contact-field-input" value={wonOpps.length} aria-label="Cars Sold" readOnly /></div>
                                    </div>
                                    <div className="contact-divider" />
                                    <div className="contact-field">
                                        <div className="field-label">Notes</div>
                                        <textarea className="notes-textarea" placeholder="Add notes…" value={notes} onChange={(e) => setNotes(e.target.value)} />
                                        <div className="notes-save"><button className="btn-sm" onClick={() => saveNotes(notes)}>Save</button></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="panel">
                            <div className="panel-header"><div className="panel-title">Activity</div></div>
                            <div className="panel-body">
                                <div className="composer-row">
                                    <select className="composer-kind" value={composerKind} onChange={(e) => setComposerKind(e.target.value)}>
                                        {ACTIVITY_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                                    </select>
                                    <input
                                        className="composer-input"
                                        placeholder="Log a call, text, email, or note…"
                                        value={composerBody}
                                        onChange={(e) => setComposerBody(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') submitActivity() }}
                                    />
                                    <button className="btn-sm" style={{ flexShrink: 0 }} disabled={!composerBody.trim()} onClick={submitActivity}>Log</button>
                                </div>
                                <div className="chip-row">
                                    {ACTIVITY_FILTERS.map((f) => (
                                        <span
                                            key={f.value}
                                            className={`chip${activityFilter === f.value ? ' active' : ''}`}
                                            onClick={() => setActivityFilter(f.value)}
                                        >{f.label}</span>
                                    ))}
                                </div>
                                <div className="feed">
                                    {visibleActivities.map((a) => (
                                        <div className="feed-item" key={a.id}>
                                            <div className={`feed-icon feed-icon-${a.kind.toLowerCase()}`}>{KIND_ICON[a.kind] || KIND_ICON.NOTE}</div>
                                            <div className="feed-content">
                                                <div className="feed-text">{a.body}</div>
                                                <div className="feed-time">{a.user_detail?.name || 'Someone'} · {shortDate(a.created_at)}</div>
                                            </div>
                                            <button className="feed-del" title="Delete" onClick={() => deleteActivity(a.id)}>×</button>
                                        </div>
                                    ))}
                                    {visibleActivities.length === 0 && (
                                        <div className="empty-mini">{activityFilter === 'all' ? 'No activity logged yet.' : 'No activity of this type.'}</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="right-col">
                        {/* Open opportunities live together with the deal-stage
                            tracker, at the top of the column. */}
                        <div className="panel">
                            <div className="quotes-header">
                                <div className="panel-title">Open Opportunities</div>
                                {openOpps.length === 0 && (
                                    <span className="opp-open-flag is-none">No open lead</span>
                                )}
                                <button className="cbtn-primary-sm" style={{ marginLeft: 'auto' }} onClick={createOpportunity}>+ New Opportunity</button>
                            </div>
                            <div className="quotes-body">
                                {openOpps.length > 0 ? (
                                    openOpps.map((opp, idx) => {
                                        const oppQuotes = opp.quotes_detail || []
                                        return (
                                            <div key={opp.id} className={`open-opp-block${idx > 0 ? ' has-sep' : ''}`}>
                                                {/* One compact toolbar row: stage progress on the
                                                    left, quote actions and the "⋯" menu on the right. */}
                                                <div className="opp-toolbar">
                                                    {stages.length > 0 && (
                                                        <StageTracker
                                                            embedded
                                                            opportunity={opp}
                                                            stages={stages}
                                                            onSetStage={updateStage}
                                                        />
                                                    )}
                                                    <div className="opp-quotes-head">
                                                        <button
                                                            className="cbtn-primary-sm"
                                                            onClick={() => openNewQuoteTab(opp.id)}
                                                        >+ New Quote</button>
                                                        <button
                                                            className="cbtn-secondary-sm"
                                                            onClick={() => setAttachOpp(opp)}
                                                        >Attach Quote</button>
                                                    </div>
                                                    <OppMenu
                                                        onMarkSold={() => setSoldOppId(opp.id)}
                                                        onMarkDead={() => markLost(opp.id)}
                                                        onDelete={() => confirmDeleteOpportunity(opp.id)}
                                                    />
                                                </div>

                                                {/* Quotes belong to this opportunity, so they live in its block. */}
                                                <div className="opp-quotes">
                                                    {oppQuotes.length
                                                        ? <div className="quotes-grid">{oppQuotes.map((q) => (
                                                            <QuoteCard
                                                                key={q.id}
                                                                quote={q}
                                                                onDelete={deleteQuote}
                                                                onSaved={() => person && usePersonDetailStore.getState().fetchPerson(person.id)}
                                                            />
                                                        ))}</div>
                                                        : <div className="empty-mini">No quotes yet — add one to start working this deal.</div>}
                                                </div>
                                            </div>
                                        )
                                    })
                                ) : (
                                    <div className="empty-mini">No open lead for this person.</div>
                                )}
                            </div>
                        </div>

                        <CurrentVehiclesSection
                            opportunities={currentWonOpps}
                            vehicles={vehicles}
                            onAddCar={() => setCarModal({ vehicle: null })}
                            onEditVehicle={(vehicle) => setCarModal({ vehicle })}
                            onDeleteVehicle={confirmDeleteVehicle}
                            onOpenOpportunityVehicle={openOpportunityVehicle}
                            onExtendVehicleLease={openVehicleLease}
                            onExtendOpportunityLease={openOpportunityLease}
                            onRemoveOpportunityVehicle={confirmRemoveOpportunityVehicle}
                            onReverseSale={confirmReverseSale}
                            onPurchaseVehicle={openVehiclePurchase}
                            onPurchaseOpportunityVehicle={openOpportunityPurchase}
                        />

                        {/* Full record of past opportunities with this person. */}
                        <div className="panel">
                            <div className="quotes-header">
                                <div className="panel-title">Lead History</div>
                            </div>
                            <div className="quotes-body">
                                {closedOpps.length
                                    ? (
                                        <LeadHistoryTable
                                            opps={sortOpps(closedOpps)}
                                            isMobile={isMobile}
                                            onReopen={reopenOpportunity}
                                            onDelete={confirmDeleteOpportunity}
                                            onOpenProfit={openProfit}
                                            onOpenTradeIn={openTradeIn}
                                            onEditDeal={(o) => setEditOppId(o.id)}
                                        />
                                    )
                                    : <div className="empty-mini">No past leads yet.</div>}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {soldOpp && <MarkSoldModal open={!!soldOpp} onClose={() => setSoldOppId(null)} opportunity={soldOpp} vehicles={vehicles} soldVehicles={currentWonOpps} />}
            {editOpp && (
                <MarkSoldModal
                    open
                    mode="edit"
                    onClose={() => setEditOppId(null)}
                    onDelete={() => confirmDeleteDeal(editOpp)}
                    onVehicleDetails={() => openTradeIn(editOpp, { fromEditDeal: true })}
                    opportunity={editOpp}
                    vehicles={vehicles}
                    soldVehicles={currentWonOpps}
                    salespeople={withOwner(editOpp.owner_detail)}
                    canReassign={canReassign}
                />
            )}
            {carModal && (
                <AddCarModal open {...carModal} onClose={() => setCarModal(null)} />
            )}
            {leaseModal && (
                <ExtendLeaseModal
                    open
                    currentDate={leaseModal.currentDate}
                    onSave={leaseModal.onSave}
                    onClose={() => setLeaseModal(null)}
                />
            )}
            {confirmAction && (
                <ConfirmDialog
                    open
                    title={confirmAction.title}
                    message={confirmAction.message}
                    confirmLabel={confirmAction.confirmLabel}
                    choices={confirmAction.choices}
                    onCancel={() => setConfirmAction(null)}
                    onConfirm={(choice) => { confirmAction.onConfirm(choice); setConfirmAction(null) }}
                />
            )}
            {attachOpp && <AttachQuotesModal open={!!attachOpp} onClose={() => setAttachOpp(null)} opportunity={attachOpp} />}
            {householdOpen && person && (
                <AddToHouseholdModal
                    open
                    person={person}
                    onClose={() => setHouseholdOpen(false)}
                    onDone={() => { setHouseholdOpen(false); usePersonDetailStore.getState().fetchPerson(person.id) }}
                />
            )}
            <EditableProfitPopover
                opportunity={opportunities.find((o) => o.id === profit.id) || null}
                anchorEl={profit.anchorEl}
                onClose={closeProfit}
                onSave={(payload) => updateOpportunityField(profit.id, payload)}
            />
        </div>
    )
}
